import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { API, useOrg } from "@/App";
import axios from "axios";
import { 
  FileText, 
  Users, 
  Package, 
  DollarSign, 
  AlertTriangle,
  Plus,
  ArrowRight,
  TrendingUp,
  Clock
} from "lucide-react";

const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentOrg } = useOrg();

  useEffect(() => {
    const fetchStats = async () => {
      if (!currentOrg) return;
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [currentOrg]);

  if (!currentOrg) {
    return (
      <div className="text-center py-16">
        <h2 className="font-outfit text-2xl font-medium mb-4">Welcome to InvoiceHub!</h2>
        <p className="text-muted-foreground mb-8">
          Get started by creating your first organization.
        </p>
        <Link to="/dashboard/organizations">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Create Organization
          </Button>
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-zinc-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-zinc-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: "Total Revenue",
      value: `$${stats?.total_revenue?.toLocaleString() || '0'}`,
      icon: DollarSign,
      color: "bg-emerald-50 text-emerald-600"
    },
    {
      label: "Outstanding",
      value: `$${stats?.total_outstanding?.toLocaleString() || '0'}`,
      icon: Clock,
      color: "bg-amber-50 text-amber-600"
    },
    {
      label: "Customers",
      value: stats?.customer_count || 0,
      icon: Users,
      color: "bg-blue-50 text-blue-600"
    },
    {
      label: "Items",
      value: stats?.item_count || 0,
      icon: Package,
      color: "bg-purple-50 text-purple-600"
    }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900">Dashboard</h1>
          <p className="text-muted-foreground text-sm">Welcome back to {currentOrg.name}</p>
        </div>
        <Link to="/dashboard/invoices/new">
          <Button className="gap-2" data-testid="create-invoice-btn">
            <Plus className="w-4 h-4" />
            New Invoice
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div 
            key={index} 
            className="bg-white border border-zinc-200 p-6"
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
            </div>
            <p className="text-2xl font-outfit font-medium">{stat.value}</p>
            <p className="text-sm text-muted-foreground">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Invoice Status & Alerts */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Invoice Status */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 p-6">
          <h2 className="font-outfit text-lg font-medium mb-4">Invoice Status</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-zinc-50">
              <p className="text-2xl font-outfit font-medium">{stats?.invoice_counts?.draft || 0}</p>
              <p className="text-sm text-muted-foreground">Draft</p>
            </div>
            <div className="text-center p-4 bg-blue-50">
              <p className="text-2xl font-outfit font-medium text-blue-600">{stats?.invoice_counts?.sent || 0}</p>
              <p className="text-sm text-blue-600">Sent</p>
            </div>
            <div className="text-center p-4 bg-emerald-50">
              <p className="text-2xl font-outfit font-medium text-emerald-600">{stats?.invoice_counts?.paid || 0}</p>
              <p className="text-sm text-emerald-600">Paid</p>
            </div>
            <div className="text-center p-4 bg-red-50">
              <p className="text-2xl font-outfit font-medium text-red-600">{stats?.invoice_counts?.overdue || 0}</p>
              <p className="text-sm text-red-600">Overdue</p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white border border-zinc-200 p-6">
          <h2 className="font-outfit text-lg font-medium mb-4">Alerts</h2>
          <div className="space-y-3">
            {stats?.low_stock_count > 0 ? (
              <div className="flex items-center gap-3 p-3 bg-amber-50 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                <div>
                  <p className="text-sm font-medium">{stats.low_stock_count} items low on stock</p>
                  <Link to="/dashboard/inventory" className="text-xs underline">View inventory</Link>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No alerts at this time</p>
            )}
            {stats?.invoice_counts?.overdue > 0 && (
              <div className="flex items-center gap-3 p-3 bg-red-50 text-red-800">
                <Clock className="w-5 h-5" />
                <div>
                  <p className="text-sm font-medium">{stats.invoice_counts.overdue} overdue invoices</p>
                  <Link to="/dashboard/invoices?status=overdue" className="text-xs underline">View invoices</Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-outfit text-lg font-medium">Recent Invoices</h2>
          <Link to="/dashboard/invoices" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        
        {stats?.recent_invoices?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Invoice</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_invoices.map((invoice) => (
                  <tr key={invoice.invoice_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-4">
                      <Link to={`/dashboard/invoices/${invoice.invoice_id}`} className="text-primary hover:underline font-medium">
                        {invoice.invoice_number}
                      </Link>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{invoice.invoice_date ? new Date(invoice.invoice_date + 'T00:00:00').toLocaleDateString('en-US') : ''}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${
                        invoice.status === 'paid' ? 'bg-emerald-50 text-emerald-700' :
                        invoice.status === 'sent' ? 'bg-blue-50 text-blue-700' :
                        invoice.status === 'draft' ? 'bg-zinc-100 text-zinc-700' :
                        'bg-amber-50 text-amber-700'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium">${invoice.grand_total?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No invoices yet</p>
            <Link to="/dashboard/invoices/new">
              <Button variant="outline" size="sm" className="gap-2">
                <Plus className="w-4 h-4" />
                Create your first invoice
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;
